import React, { useState, useEffect, useCallback } from 'react';

import SoftCard from '../../SoftCard';
import TableWithDropdown from '../../TableWithDropdown';

import { getFamilyMembers } from '../../../../utils/familyUtils';
import { getLatestOutstanding } from '../../../../utils/helperFunctions';
import styles from './UnpaidEventList.module.scss';


/**
 * 
 * @param {object} user  - the user object, should have at least an id and first_name, last_name
 * @param {string} type - the type of display, either "list", "card", or table
 * @returns 
 */
export const UnpaidEventList = ({
    user,
    type="list",
    title="PENDING CHARGES",
    subtitle="",
    icons=true,
    labels=true,
    includeFamily=true,
    adminOverride=false,
    border=true,
    maxWidth="250px",
    additionalDetails=null,
    loadCallback=null
}) => {

    const [ charges, setCharges] = useState([]);
    const [ loading, setLoading ] = useState(true);

    const handleCharges = useCallback((charges)=>{
        let alteredCharges = [];
        let i = 0;
        charges?.forEach((member)=>{
            let j = 0;
            member?.charges?.forEach((charge)=>{
                charge.for_user_id = member.id
                charge.for_user_name = member.name
                alteredCharges.push(charge)
                charge.paid = charge.payment_totals?.total_paid;
                charge.price = charge.payment_totals?.final_price;
                charge.id = `${charge.event_id}-${j}-${member.id}-${i}`;
                j++
            })
            i++
        })
        return alteredCharges;
    },[])

    const getCharges = useCallback(async()=>{
        if(user?.id){
            let charges;
            if(includeFamily){
                let family = await getFamilyMembers(user.id, "authOnly", adminOverride )
                charges = await getLatestOutstanding(user.id, user, null, family);
            }else{
                charges = await getLatestOutstanding(user.id, user, adminOverride);
            }
            setLoading(true);
            let adjusted = handleCharges(charges)
            setCharges(adjusted);
            setLoading(false);
            if(loadCallback) loadCallback(adjusted);
        }
    },[user, handleCharges, adminOverride, includeFamily, loadCallback])

    useEffect(()=>{
        getCharges();
    },[getCharges])

    const tableHeadings=[
        {key: "event_name", label: "Event"}, 
        {key: "for_user_name", label: "For User"},       
        {key: "order_id", label: "Order ID"},
        {key: "price", label: "Price"},
        {key: "paid", label: "Paid"},
    ]

    return (
        <div
            style={{maxWidth: maxWidth}} 
            className={`
                ${styles["unpaid-event-list-wrapper"]}
                ${border ? styles["border"] : ""}
            `}
        >
            {title &&
                <h3 className={styles.title}>
                    {title}
                </h3>
            }
            {subtitle &&
                <p className={styles["subtitle"]}>
                    {subtitle}
                </p>
            }
            {loading ? 
                <p>Loading...</p>
            :
                <div>
                    {type==="list" &&
                        <>
                            {charges?.map((charge, i)=>(
                                <ul key={`charge-${charge.id}-${i}`}>
                                    <li>
                                        {icons &&
                                            <i className="fal fa-calendar-alt" />
                                        }
                                        {labels &&
                                            <span className={styles["list-label"]}>
                                                Event:
                                            </span>
                                        }
                                        {charge.event_name}
                                    </li>
                                    <li>
                                        {icons &&
                                            <i className="fal fa-user" />
                                        }
                                        {labels &&
                                            <span className={styles["list-label"]}>
                                                For:
                                            </span>
                                        }
                                        {charge.for_user_name}
                                    </li>
                                    <li>
                                        {icons &&
                                            <i className="fal fa-tags" />
                                        }
                                        {labels &&
                                            <span className={styles["list-label"]}>
                                                Order ID:
                                            </span>
                                        }
                                        {charge.order_id || "No order started"}
                                    </li>
                                    <li>
                                        {icons &&
                                            <i className="fal fa-file-invoice-dollar" />
                                        }
                                        {labels &&
                                            <span className={styles["list-label"]}>
                                                Paid/Price:
                                            </span>
                                        }
                                        ${charge.paid}/${charge.price}
                                    </li>
                                    {additionalDetails}
                                </ul>
                            ))}
                        </>
                    }
                    {type==="card" &&
                        <div className={styles["card-list"]}>
                            {charges?.map((charge, i)=>(
                                <SoftCard key={`charge-${charge.id}-${i}`}>
                                    <SoftCard.Header>
                                        <span className={styles["card-label"]}>
                                            {charge.event_name}
                                        </span>
                                    </SoftCard.Header>
                                    <p>
                                        {icons &&
                                            <i className="fal fa-user" />
                                        }
                                        {labels &&
                                            <span className={styles["list-label"]}>
                                                For:
                                            </span>
                                        }
                                        {charge.for_user_name}
                                    </p>
                                    <p>
                                        {icons &&
                                            <i className="fal fa-tags" />
                                        }
                                        {labels &&
                                            <span className={styles["list-label"]}>
                                                Order ID:
                                            </span>
                                        }
                                        {charge.order_id || "No order"}
                                    </p>
                                    <p>
                                        {icons &&
                                            <i className="fal fa-file-invoice-dollar" />
                                        }
                                        {labels &&
                                            <span className={styles["list-label"]}>
                                                Paid/Price:
                                            </span>
                                        }
                                        ${charge.paid}/${charge.price}
                                    </p>
                                    {additionalDetails}
                                </SoftCard>

                            ))}
                        </div>
                    }
                    {type==="table" &&
                        <TableWithDropdown
                            tableHeadings={tableHeadings}
                            data={charges}
                            {...additionalDetails}
                        />        
                    }
                </div>
            }
            
        </div>
    );
};